import { useEffect, useState } from 'react';
import { Button, Input } from '../ui';
import { useToast } from '../Toast';
import { fetchPrices, createPrice, formatCents, type PriceRow } from '../../lib/billing-admin-queries';

export function BillingPanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pricing state
  const [activePrices, setActivePrices] = useState<{ monthly: PriceRow | null; annual: PriceRow | null }>({ monthly: null, annual: null });
  const [history, setHistory] = useState<PriceRow[]>([]);
  const [monthlyDraft, setMonthlyDraft] = useState('');
  const [annualDraft, setAnnualDraft] = useState('');
  const [savingInterval, setSavingInterval] = useState<'monthly' | 'annual' | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const prices = await fetchPrices();
      setActivePrices(prices.active);
      setHistory(prices.history);
      // Seed drafts with current values so admin sees what's set.
      setMonthlyDraft(prices.active.monthly ? (prices.active.monthly.unit_amount_cents / 100).toString() : '');
      setAnnualDraft(prices.active.annual ? (prices.active.annual.unit_amount_cents / 100).toString() : '');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load billing.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function handleSavePrice(interval: 'monthly' | 'annual', draft: string) {
    const dollars = Number.parseFloat(draft);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    const amount_cents = Math.round(dollars * 100);
    if (amount_cents < 100) {
      toast.error('Amount must be at least $1.');
      return;
    }

    const current = activePrices[interval];
    const currentLabel = current ? formatCents(current.unit_amount_cents, current.currency) : '(none set)';
    const newLabel = formatCents(amount_cents);

    const ok = window.confirm(
      `Change ${interval} price from ${currentLabel} to ${newLabel}?\n\n` +
      `• Existing subscribers stay on their current price (no impact)\n` +
      `• Anyone who signs up from now on will be charged ${newLabel}\n\n` +
      `This creates a new Price in Stripe and archives the old one in our records. ` +
      `It cannot be undone via this form: you'd need to set the price back manually.`,
    );
    if (!ok) return;

    setSavingInterval(interval);
    try {
      await createPrice({ interval, amount_cents });
      toast.success(`${interval[0].toUpperCase()}${interval.slice(1)} price updated to ${newLabel}.`);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update price.');
    } finally {
      setSavingInterval(null);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Billing</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Billing</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Pricing -------------------------------------------------------- */}
      <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
        <h3 style={{ margin: 0 }}>Subscription pricing</h3>
      </div>
      <p className="admin__note">
        Changing a price creates a new Stripe Price for new signups. Existing subscribers are
        grandfathered: they continue to be charged whatever they originally signed up for, until
        they cancel or you move them manually in the Stripe Dashboard.
      </p>

      <div className="admin__price-list">
        <div className="admin__price-row">
          <label className="admin__price-field">
            <span>
              Monthly price (USD) · currently{' '}
              {activePrices.monthly ? formatCents(activePrices.monthly.unit_amount_cents) : 'not set'}
            </span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="1"
              placeholder="15.00"
              value={monthlyDraft}
              onChange={(e) => setMonthlyDraft(e.target.value)}
              disabled={savingInterval !== null}
            />
          </label>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => handleSavePrice('monthly', monthlyDraft)}
            disabled={savingInterval !== null || !monthlyDraft}
          >
            {savingInterval === 'monthly' ? '…' : 'Save monthly'}
          </Button>
        </div>

        <div className="admin__price-row">
          <label className="admin__price-field">
            <span>
              Annual price (USD) · currently{' '}
              {activePrices.annual ? formatCents(activePrices.annual.unit_amount_cents) : 'not set'}
            </span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="1"
              placeholder="150.00"
              value={annualDraft}
              onChange={(e) => setAnnualDraft(e.target.value)}
              disabled={savingInterval !== null}
            />
          </label>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => handleSavePrice('annual', annualDraft)}
            disabled={savingInterval !== null || !annualDraft}
          >
            {savingInterval === 'annual' ? '…' : 'Save annual'}
          </Button>
        </div>
      </div>

      {/* History ------------------------------------------------------- */}
      {history.length > 0 && (
        <>
          <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
            <h3 style={{ margin: 0 }}>Price history</h3>
            <span className="admin__count">{history.length}</span>
          </div>
          <table className="admin__table">
            <thead>
              <tr>
                <th>Interval</th>
                <th>Tier</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Stripe Price ID</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id}>
                  <td>{p.interval}</td>
                  <td>{p.tier}</td>
                  <td>{formatCents(p.unit_amount_cents, p.currency)}</td>
                  <td>
                    <span className={`pill pill--${p.is_active ? 'active' : 'invited'}`}>
                      {p.is_active ? 'active' : 'archived'}
                    </span>
                  </td>
                  <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td><code style={{ fontSize: '0.75em' }}>{p.stripe_price_id}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Founding members live in the Members tab. */}
      <div className="admin__panel-head" style={{ marginTop: 'var(--space-8)' }}>
        <h3 style={{ margin: 0 }}>Founding members</h3>
      </div>
      <p className="admin__note">
        Founding members are managed from the <strong>Members</strong> tab: toggle a member's
        <strong> Founding member</strong> status and they'll bypass the paywall (comped subscription).
      </p>
    </>
  );
}
